import React, {useState, useEffect} from "react";
import {useForm} from "react-hook-form";
import {InfinitySpin} from "react-loader-spinner";
import {toast} from "react-toastify";
import {LoginButton} from "../../styles/Styles";
import {Languages} from "../common/languages";
import {useMutation, useQuery} from "@apollo/client";
import {GET_TOUR_ITEMS_LIST, GET_TOURS_LIST} from "../gql/tours-gql";
import {ADD_BOOKING, EDIT_BOOKING} from "../gql/bookings-gql";
import DatePicker from "react-datepicker";
import {ReservationSystem} from "../common/bookingSystel";

type Inputs = {
    price: number;
    bookingDate: Date;
    purchaseDate: Date;
    tourItem: string;
    language: Languages;
    availability: boolean;
    bookingCode: string;
    clientName: string;
    clientPhone: string;
    clientAdult: number;
    clientChild: number;
    clientEnfant: number;
    clientYouth: number;
    priceNet: number;
    bookingSystem: string;
}

interface Label {
    _id: string;
    title: string;
}

interface BookingData {
    _id?: string;
    tourItem: {
        _id: string;
        title: string;
    }
    bookingCode: string;
    client: {
        clientName: string;
        clientPhone: string;
        clientAdult: number;
        clientChild: number;
        clientEnfant: number;
        clientYouth: number;
    };
    price: number;
    priceNet: number;
    bookingDate: Date;
    purchaseDate: Date;
    bookingSystem: string;
    language: Languages[];
}

interface AddBookingFormProps {
    bookingToEdit?: BookingData;
    onClose?: () => void;
}

const AddBookingForm: React.FC<AddBookingFormProps> = ({ bookingToEdit, onClose }) => {
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [purchasetDate, setPurchasetDate] = useState<Date>(new Date());
    const toursListVars = {
        month: startDate.getMonth() + 1,
        year: startDate.getFullYear()
    };

    const {data: toursItems, loading: toursLoading} = useQuery(GET_TOUR_ITEMS_LIST);
    const labels = toursItems?.getTourItemsList || [];

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm<Inputs>({
        defaultValues: {
            clientAdult: 0,
            clientChild: 0,
            clientEnfant: 0,
            clientYouth: 0
        }
    });

    const LanguagesArray = Object.values(Languages);

    const [addBookingMutation] = useMutation(ADD_BOOKING, {
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursListVars }],
        onCompleted: (data) => {
            toast.success("Added successfully!");
            reset();
            onClose?.();
        },
        onError: (error) => {
            if (error.message.includes('not_uniq')) {
                toast.error("This booking code already exists!");
            } else {
                toast.error("Failed to add booking: " + error.message);
            }
        }
    });

    const [editBookingMutation] = useMutation(EDIT_BOOKING, {
        refetchQueries: [{ query: GET_TOURS_LIST, variables: toursListVars }],
        onCompleted: (data) => {
            toast.success("Updated successfully!");
            reset();
            onClose?.();
        },
        onError: (error) => {
            if (error.message.includes('not_uniq')) {
                toast.error("This booking code already exists!");
            } else {
                toast.error("Failed to update booking: " + error.message);
            }
        }
    });

    useEffect(() => {
        if (bookingToEdit) {
            // Set form values from bookingToEdit
            setValue('tourItem', bookingToEdit.tourItem.title);
            setValue('bookingCode', bookingToEdit.bookingCode);
            setValue('clientName', bookingToEdit.client.clientName);
            setValue('clientPhone', bookingToEdit.client.clientPhone);
            setValue('clientAdult', bookingToEdit.client.clientAdult);
            setValue('clientChild', bookingToEdit.client.clientChild);
            setValue('clientEnfant', bookingToEdit.client.clientEnfant);
            setValue('clientYouth', bookingToEdit.client.clientYouth);
            setValue('price', bookingToEdit.price);
            setValue('priceNet', bookingToEdit.priceNet);
            setValue('bookingSystem', bookingToEdit.bookingSystem);
            setValue('language', bookingToEdit.language[0]);

            setStartDate(new Date(bookingToEdit.bookingDate));
            setPurchasetDate(new Date(bookingToEdit.purchaseDate));
        }
    }, [bookingToEdit, setValue]);

    const onSubmit = async(data: any) => {
        const client = {
            clientName: data.clientName,
            clientCountry: "",
            clientPhone: data.clientPhone,
            clientEmail: "email@gmail.com",
            clientAdult: parseFloat(data.clientAdult),
            clientChild: parseFloat(data.clientChild),
            clientEnfant: parseFloat(data.clientEnfant),
            clientYouth: parseFloat(data.clientYouth)
        }

        const bookingInput = {
            bookingCode: data.bookingCode,
            tourItem: data.tourItem,
            client,
            price: parseFloat(data.price),
            priceNet: parseFloat(data.priceNet),
            bookingDate: startDate,
            purchaseDate: purchasetDate,
            bookingSystem: data.bookingSystem,
            option: "",
            language: [data.language]
        };

        if (bookingToEdit?._id) {
            await editBookingMutation({
                variables: {
                    bookingInput: {
                        ...bookingInput,
                        _id: bookingToEdit._id
                    }
                }
            });
        } else {
            await addBookingMutation({
                variables: {
                    bookingInput
                }
            });
        }
    }

    if (toursLoading) return <InfinitySpin />;

    return (<div>
        <form onSubmit={handleSubmit(onSubmit)} className="form-v" style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "700px", margin: "0 auto" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="tourItem">Tour Label</label>
                <select {...register("tourItem", { required: true })} style={{ padding: "0.5rem" }}>
                    {labels.map((label: Label, index: number) => (<option key={index} value={label._id}>{label.title}</option>))}
                </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="bookingDate">Visit date</label>
                <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => date && setStartDate(date)}
                    showTimeSelect
                    className="date-picker"
                />
            </div>

            <h2>CLIENT</h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="clientName">Client Name</label>
                <input {...register("clientName", { required: true })} type="text" placeholder="Client Name" style={{ padding: "0.5rem" }} />
                {errors.clientName && <span className="error">This field is required</span>}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="clientPhone">Client Phone</label>
                <input {...register("clientPhone", { required: true })} type="text" placeholder="Client Phone" style={{ padding: "0.5rem" }} />
                {errors.clientPhone && <span className="error">This field is required</span>}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="clientAdult">Adults</label>
                <input 
                    {...register("clientAdult", { 
                        required: true,
                        min: 0,
                        max: 100,
                        valueAsNumber: true
                    })} 
                    type="number" 
                    placeholder="Number of adults" 
                    style={{ padding: "0.5rem" }} 
                />
                {errors.clientAdult && <span className="error">
                    {errors.clientAdult.type === 'required' ? 'This field is required' : 
                     errors.clientAdult.type === 'min' || errors.clientAdult.type === 'max' ? 
                     'Value must be between 0 and 100' : 'Invalid value'}
                </span>}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="clientChild">Children</label>
                <input 
                    {...register("clientChild", { 
                        required: false,
                        min: 0,
                        max: 100,
                        valueAsNumber: true
                    })} 
                    type="number" 
                    placeholder="Number of children" 
                    style={{ padding: "0.5rem" }} 
                />
                {errors.clientChild && <span className="error">
                    {errors.clientChild.type === 'min' || errors.clientChild.type === 'max' ? 
                     'Value must be between 0 and 100' : 'Invalid value'}
                </span>}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="clientEnfant">Enfants</label>
                <input 
                    {...register("clientEnfant", { 
                        required: false,
                        min: 0,
                        max: 100,
                        valueAsNumber: true
                    })} 
                    type="number" 
                    placeholder="Number of enfants" 
                    style={{ padding: "0.5rem" }} 
                />
                {errors.clientEnfant && <span className="error">
                    {errors.clientEnfant.type === 'min' || errors.clientEnfant.type === 'max' ? 
                     'Value must be between 0 and 100' : 'Invalid value'}
                </span>}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="clientYouth">Youths</label>
                <input 
                    {...register("clientYouth", { 
                        required: false,
                        min: 0,
                        max: 100,
                        valueAsNumber: true
                    })} 
                    type="number" 
                    placeholder="Number of youths" 
                    style={{ padding: "0.5rem" }} 
                />
                {errors.clientYouth && <span className="error">
                    {errors.clientYouth.type === 'min' || errors.clientYouth.type === 'max' ? 
                     'Value must be between 0 and 100' : 'Invalid value'}
                </span>}
            </div>

            <h2>Tour Details</h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="language">Language</label>
                <select {...register("language", { required: true })} style={{ padding: "0.5rem" }}>
                    {LanguagesArray.map((lang, index) => (<option value={lang} key={index}>{lang}</option>))}
                </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="bookingCode">Reservation system's code</label>
                <input {...register("bookingCode", { required: true })} placeholder="Booking code" type="text" style={{ padding: "0.5rem" }} />
                {errors.bookingCode && <span className="error">This field is required</span>}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="price">Client paid</label>
                <input {...register("price", { required: false })} type="number" placeholder="Price" style={{ padding: "0.5rem" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="priceNet">Paid to us</label>
                <input {...register("priceNet", { required: false })} type="number" placeholder="Net price" style={{ padding: "0.5rem" }} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label>Purchase date</label>
                <DatePicker
                    selected={purchasetDate}
                    onChange={(date: Date | null) => date && setPurchasetDate(date)}
                    className="date-picker"
                />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <label htmlFor="bookingSystem">Booking System</label>
                <select {...register("bookingSystem", { required: true })} style={{ padding: "0.5rem" }}>
                    {Object.keys(ReservationSystem).map((el, index: number) => (el !== ReservationSystem.ALL && <option key={index} value={el}>{el}</option>))}
                </select>
            </div>

            <LoginButton type="submit" style={{ marginTop: "1rem" }}>{bookingToEdit ? 'Update Booking' : 'Add Booking'}</LoginButton>
        </form>
    </div>);
}

export default AddBookingForm;
